import { renderPageShell } from '../lib/page-shell';
import { workerHttpUrl, workerSocketUrl } from '../lib/worker-config';
import { getToken, mintConnectionTicket } from '../lib/session';
import { attachDuelSession } from '../lib/duel-session';
import type { RouteHandler } from '../lib/router';

export const renderOnlineDuel: RouteHandler = (container, navigate, query) => {
  const body = renderPageShell(container, navigate, 'play', {
    title: 'ONLINE CUSTOM MATCH',
    subtitle: 'Create a room and share the code, or join one. Best of five, server-authoritative.',
    body: `<div id="online-mount"></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#online-mount')!;
  let socket: WebSocket | null = null;
  let destroySession: (() => void) | void;

  function cleanup() {
    if (destroySession) destroySession();
    destroySession = undefined;
    if (socket) {
      socket.onclose = null;
      socket.close();
      socket = null;
    }
  }

  function renderMenu() {
    cleanup();
    mount.innerHTML = `
      <div class="choice-grid">
        <div class="choice-card">
          <strong>Create a match</strong>
          <small>Get a room code, share it with a friend.</small>
          <button class="button button--primary" type="button" data-action="create">Create match</button>
        </div>
        <div class="choice-card">
          <strong>Join a match</strong>
          <small>Enter the code your friend shared.</small>
          <input class="join-input" id="join-code" maxlength="8" placeholder="ROOM CODE" aria-label="Room code" autocomplete="off" />
          <button class="button button--secondary" type="button" data-action="join">Join match</button>
        </div>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="create"]')?.addEventListener('click', handleCreate);
    mount.querySelector<HTMLButtonElement>('[data-action="join"]')?.addEventListener('click', () => {
      const input = mount.querySelector<HTMLInputElement>('#join-code');
      const code = input?.value.trim().toUpperCase();
      if (code) connectToRoom(code);
    });
  }

  function renderConnecting(message: string) {
    mount.innerHTML = `<div class="handoff"><p class="handoff__label">${message}</p></div>`;
  }

  async function connectToRoom(code: string) {
    renderConnecting(`Connecting to ${code}…`);
    cleanup();
    // Signed-in players connect with a single-use ticket (never the session
    // token). Guests have no session and connect anonymously, as before.
    let connectionTicket: string | null = null;
    if (getToken()) {
      try {
        connectionTicket = await mintConnectionTicket();
      } catch {
        connectionTicket = null; // fall back to a guest connection rather than failing
      }
    }
    socket = new WebSocket(workerSocketUrl(code, { connectionTicket }));
    socket.onclose = () => {
      socket = null;
    };
    destroySession = attachDuelSession(mount, socket, {
      lobbyLabel: 'Room code',
      lobbyHeading: code,
      onExit: () => {
        cleanup();
        renderMenu();
      },
    });
  }

  async function handleCreate() {
    renderConnecting('Creating a room…');
    try {
      const response = await fetch(`${workerHttpUrl()}/api/match`, { method: 'POST' });
      if (!response.ok) throw new Error(`server responded ${response.status}`);
      const data = (await response.json()) as { code: string };
      connectToRoom(data.code);
    } catch {
      mount.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Error</p>
          <h2>Could not reach the match server. Is it running?</h2>
          <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
        </div>
      `;
      mount.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', renderMenu);
    }
  }

  // Arriving from an accepted direct challenge (`play/online?code=XYZ`) drops the
  // player straight into that room; otherwise show the create/join menu.
  const presetCode = query?.get('code')?.trim().toUpperCase();
  if (presetCode) {
    void connectToRoom(presetCode);
  } else {
    renderMenu();
  }

  return () => cleanup();
};
